import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, Surface } from "../context/AuthContext";

/**
 * Shown when a signed-in account reaches a surface it has no grant for —
 * a staff login navigating to /crm being the canonical case.
 *
 * The page states the refusal plainly and offers exactly one action: file an
 * access request an admin will see. It does not pretend the surface is empty
 * or broken, because "access declined" the user can act on beats a mystery.
 */
export const AccessDenied = ({ surface }: { surface: Surface }) => {
    const { user, mayAccess } = useAuth();
    const navigate = useNavigate();
    const [state, setState] = useState<"idle" | "sending" | "sent" | "already">("idle");
    const [error, setError] = useState<string | null>(null);

    const surfaceName = surface === "crm" ? "the CRM" : "ShiftFlow";
    const home = mayAccess("shiftflow") ? "/" : "/crm";

    const request = async () => {
        setState("sending");
        setError(null);
        try {
            const result = await api.post<{ alreadyRequested: boolean }>("/access/request", { surface });
            setState(result.alreadyRequested ? "already" : "sent");
        } catch (err) {
            setState("idle");
            setError(err instanceof Error ? err.message : "Could not send the request");
        }
    };

    return (
        <div className="denied">
            <img src="/brand/icon.png" alt="" />
            <h2>Access declined</h2>
            <p>
                Your account (<strong>{user?.email}</strong>, role <strong>{user?.role}</strong>) does not
                have access to {surfaceName}. This is a deliberate restriction, not an error.
            </p>

            {error && <div className="error-banner">{error}</div>}

            {state === "sent" && (
                <div className="alert-banner" style={{ textAlign: "left" }}>
                    <h3>Request sent</h3>
                    <p style={{ fontSize: "0.83rem" }}>
                        An administrator will review it. Access takes effect the next time you sign in
                        after it is approved.
                    </p>
                </div>
            )}
            {state === "already" && (
                <p><strong>You already have a request waiting.</strong> An administrator has not decided it yet.</p>
            )}

            <div style={{ display: "flex", gap: "0.5rem" }}>
                {(state === "idle" || state === "sending") && (
                    <button className="btn" onClick={request} disabled={state === "sending"}>
                        {state === "sending" ? "Sending…" : `Request ${surface === "crm" ? "CRM" : "ShiftFlow"} access`}
                    </button>
                )}
                <button className="btn ghost" onClick={() => navigate(home)}>
                    Back to {mayAccess("shiftflow") ? "ShiftFlow" : "CRM"}
                </button>
            </div>
        </div>
    );
};
